import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Building2, ChevronRight, Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import AppShell from "@/components/layout/AppShell";
import useOrganizations from "../../hooks/useOrganizations";
import { matchesName } from "../../utils/search";
import CreateOrganizationModal from "./CreateOrganizationModal";

const OrganizationSelect: React.FC = () => {
  const navigate = useNavigate();
  const { organizations, error, reload } = useOrganizations();
  const [creating, setCreating] = useState(false);
  const [query, setQuery] = useState("");
  const visible = organizations?.filter((o) => matchesName(o.name, query));

  return (
    <AppShell breadcrumbs={[{ label: "Organisaties" }]}>
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 p-4 md:p-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Organisaties</h1>
            <p className="text-sm text-muted-foreground">
              Kies een organisatie om haar collecties en leden te beheren.
            </p>
          </div>
          <Button onClick={() => setCreating(true)}>
            <Plus />
            Nieuwe organisatie
          </Button>
        </div>

        <div className="relative w-full sm:w-64">
          <Search
            className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            id="organization-search"
            type="search"
            placeholder="Zoek organisatie…"
            aria-label="Zoek organisatie"
            className="h-8 pl-8"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        {error ? (
          <p className="rounded-xl border border-dashed px-6 py-12 text-center text-sm text-destructive">
            {error.message}
          </p>
        ) : visible == null ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }, (_, i) => (
              <Skeleton key={i} className="h-16 rounded-xl" />
            ))}
          </div>
        ) : visible.length === 0 ? (
          <p className="rounded-xl border border-dashed px-6 py-12 text-center text-sm text-muted-foreground">
            {query ? "Geen organisaties gevonden" : "Nog geen organisaties"}
          </p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {visible.map((organization) => (
              <Link
                key={organization._id}
                to={`/${organization._id}`}
                className="group flex items-center gap-3 rounded-xl border bg-card p-4 shadow-xs outline-none transition-shadow hover:shadow-md focus-visible:ring-[3px] focus-visible:ring-ring/50"
              >
                <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                  <Building2 className="size-4" />
                </span>
                <span className="flex-1 truncate font-medium">{organization.name}</span>
                <ChevronRight className="size-4 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
              </Link>
            ))}
          </div>
        )}
      </div>
      <CreateOrganizationModal
        open={creating}
        onClose={(created) => {
          setCreating(false);
          if (created) {
            reload();
            navigate(`/${created._id}`);
          }
        }}
      />
    </AppShell>
  );
};

export default OrganizationSelect;
